import { StandardEvents } from '@shopify/events';

const stateByProduct = new Map();
const galleryStateBySection = new WeakMap();
const getChooseText = (picker) => picker?.dataset.chooseText ?? '';

function getSection(element) {
  return element?.closest('.shopify-section');
}

function getProductId(picker) {
  return picker?.dataset.productId || '';
}

function getState(picker) {
  const productId = getProductId(picker);
  if (!productId) return new Map();

  if (!stateByProduct.has(productId)) {
    stateByProduct.set(productId, new Map());
  }

  return stateByProduct.get(productId);
}

function getOptionNames(picker) {
  const names = new Set();
  if (!picker) return names;
  picker.querySelectorAll('[data-option-name]').forEach((option) => {
    if (option.dataset.optionName) names.add(option.dataset.optionName);
  });
  return names;
}

function getGalleryState(section) {
  if (!section) return { selectionVersion: 0, lastOptionValueId: '' };

  if (!galleryStateBySection.has(section)) {
    galleryStateBySection.set(section, {
      selectionVersion: 0,
      lastOptionValueId: '',
    });
  }

  return galleryStateBySection.get(section);
}

function rememberGalleryIntent(section, optionValueId) {
  const galleryState = getGalleryState(section);
  galleryState.selectionVersion += 1;
  galleryState.lastOptionValueId = optionValueId || '';
  return galleryState.selectionVersion;
}

function seedPicker(picker) {
  const state = getState(picker);
  if (state.size || picker.dataset.ctSeeded === 'true') return;

  const hasExplicitVariant = new URL(window.location.href).searchParams.has('variant');
  if (hasExplicitVariant || picker.dataset.ctUnselected !== 'true') {
    picker.querySelectorAll('input:checked, option:checked').forEach((option) => {
      if (option.dataset.optionName && option.dataset.optionValueId) {
        state.set(option.dataset.optionName, option.dataset.optionValueId);
      }
    });
  }

  picker.dataset.ctSeeded = 'true';
}

function rememberOption(option) {
  const picker = option.closest('variant-picker');
  if (!picker || !option.dataset.optionName || !option.dataset.optionValueId) return;

  getState(picker).set(option.dataset.optionName, option.dataset.optionValueId);
}

function setLegend(field, chosenLabel) {
  const legend = field.querySelector('legend');
  if (!legend) return;

  const optionName = legend.dataset.optionLabel || field.querySelector('[data-option-name]')?.dataset.optionName;
  if (!optionName) return;

  const swatchValue = legend.querySelector('.variant-option__swatch-value');
  legend.firstChild.textContent = `${optionName}: ${chosenLabel || getChooseText(field.closest('variant-picker'))}`;
  if (swatchValue) swatchValue.textContent = chosenLabel || '';
}

function applyPickerState(picker) {
  seedPicker(picker);
  const state = getState(picker);

  picker.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
    const optionName = fieldset.querySelector('[data-option-name]')?.dataset.optionName;
    if (!optionName) return;

    const selectedValueId = state.get(optionName);
    let selectedLabel = '';

    fieldset.querySelectorAll('input[data-option-value-id]').forEach((input) => {
      const checked = Boolean(selectedValueId) && input.dataset.optionValueId === selectedValueId;
      input.checked = checked;
      input.dataset.currentChecked = checked ? 'true' : 'false';
      input.dataset.previousChecked = 'false';
      if (checked) selectedLabel = input.value;
    });

    setLegend(fieldset, selectedLabel);
  });

  picker.querySelectorAll('select.variant-option__select').forEach((select) => {
    const optionName = select.querySelector('[data-option-name]')?.dataset.optionName;
    if (!optionName) return;

    const selectedValueId = state.get(optionName);
    const selectedOption = Array.from(select.options).find((option) => option.dataset.optionValueId === selectedValueId);
    if (selectedOption) {
      select.value = selectedOption.value;
    } else {
      select.selectedIndex = 0;
    }
  });

  // The re-rendered picker rated availability against the server's full variant, not these choices.
  picker.updateOptionAvailability?.();
}

function isSelectionComplete(picker) {
  if (!picker) return false;
  const state = getState(picker);
  return state.size >= getOptionNames(picker).size;
}

function setButtonText(button, text) {
  const content = button.querySelector('.add-to-cart-text__content');
  const textNode = content?.querySelector(':scope > span > span') || content?.querySelector(':scope > span');
  if (textNode) textNode.textContent = text;
}

function applyAddToCartGate(section) {
  const picker = section.querySelector('variant-picker');
  if (!picker) return;

  const incomplete = !isSelectionComplete(picker);
  section.querySelectorAll('product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (incomplete) {
      button.disabled = true;
      setButtonText(button, getChooseText(picker));
    }
  });

  const sticky = section.querySelector('sticky-add-to-cart');
  if (sticky) sticky.dataset.ctIncompleteSelection = String(incomplete);

  section.querySelectorAll('product-inventory').forEach((inventory) => {
    inventory.hidden = incomplete;
  });
}

function applySectionState(section) {
  const picker = section.querySelector('variant-picker');
  if (!picker) return;

  applyPickerState(picker);
  applyAddToCartGate(section);
}

function getGallerySlides(gallery) {
  return gallery ? Array.from(gallery.querySelectorAll('slideshow-slide')) : [];
}

function slideMatchesOptionValue(element, optionValueId) {
  const ids = element.dataset.ctOptionValueIds;
  if (ids) return ids.split(' ').includes(optionValueId);

  return element.dataset.ctOptionValueId === optionValueId;
}

function getGallerySlideIndex(gallery, optionValueId) {
  if (!gallery || !optionValueId) return -1;

  return getGallerySlides(gallery).findIndex((slide) => slideMatchesOptionValue(slide, optionValueId));
}

function getGalleryFallbackSlideIndex(gallery) {
  if (!gallery) return -1;

  const declared = Number(gallery.dataset.ctFallbackSlideIndex);
  const slides = getGallerySlides(gallery);
  if (Number.isInteger(declared) && declared >= 0 && declared < slides.length) return declared;

  // Older markup (or a gallery rendered before the attribute existed): the first
  // slide that isn't tied to an option value is the generic product image.
  const generic = slides.findIndex((slide) => !slide.dataset.ctOptionValueIds && !slide.dataset.ctOptionValueId);
  if (generic >= 0) return generic;

  return slides.length ? 0 : -1;
}

function syncGallerySelection(gallery, optionValueId, slideIndex) {
  if (!gallery || !optionValueId || slideIndex < 0) return;

  gallery.dataset.ctSelectedOptionValueId = optionValueId;
  gallery.slideshow?.setAttribute('initial-slide', String(slideIndex));
  gallery.slideshow?.select(slideIndex, undefined, { animate: false });

  const thumbnail = gallery.querySelector(
    `.slideshow-controls__thumbnail[data-ct-option-value-ids~="${CSS.escape(optionValueId)}"],
     .slideshow-controls__thumbnail[data-ct-option-value-id="${CSS.escape(optionValueId)}"]`
  );
  thumbnail?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function selectGalleryItem(target) {
  const section = getSection(target);
  const gallery = target.closest('media-gallery');
  const optionValueId = target.dataset.ctOptionValueId;
  if (!section || !optionValueId) return;

  const slide = target.closest('slideshow-slide');
  const slides = getGallerySlides(gallery);
  const slideIndex = slide ? slides.indexOf(slide) : Number(target.getAttribute('on:click')?.match(/select\/(\d+)/)?.[1]);
  if (gallery && Number.isInteger(slideIndex) && slideIndex >= 0) {
    syncGallerySelection(gallery, optionValueId, slideIndex);
  }

  const input = section.querySelector(`variant-picker input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
  if (!input) return;

  rememberOption(input);
  if (input.checked) return;

  input.checked = true;
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function restoreGallery(section, requestVersion) {
  const gallery = section.querySelector('media-gallery');
  const picker = section.querySelector('variant-picker');
  if (!gallery || !picker) return;

  const galleryState = getGalleryState(section);
  const isCurrentRequest = requestVersion === galleryState.selectionVersion;
  const candidates = [];

  if (isCurrentRequest && gallery.dataset.ctSelectedOptionValueId) {
    candidates.push(gallery.dataset.ctSelectedOptionValueId);
  }

  if (galleryState.lastOptionValueId) {
    candidates.push(galleryState.lastOptionValueId);
  }

  for (const optionValueId of getState(picker).values()) {
    candidates.push(optionValueId);
  }

  const selectedOptionValueId = candidates.find((optionValueId, index) => {
    if (!optionValueId || candidates.indexOf(optionValueId) !== index) return false;
    return getGallerySlideIndex(gallery, optionValueId) >= 0;
  });

  if (!selectedOptionValueId) {
    // Nothing the shopper picked maps to a slide — typically a variant published
    // without a variant image. Show the fallback rather than the stale slide the
    // previous selection left behind.
    if (isCurrentRequest) showFallbackSlide(gallery, galleryState.lastOptionValueId);
    return;
  }

  syncGallerySelection(gallery, selectedOptionValueId, getGallerySlideIndex(gallery, selectedOptionValueId));
}

function showOptionInGallery(section, optionValueId) {
  const gallery = section?.querySelector('media-gallery');
  if (!gallery || !optionValueId) return;

  const slideIndex = getGallerySlideIndex(gallery, optionValueId);
  if (slideIndex >= 0) {
    syncGallerySelection(gallery, optionValueId, slideIndex);
    return;
  }

  // The option value has no image of its own. Leaving the gallery untouched would
  // show whatever was selected before, so show the fallback slide instead.
  showFallbackSlide(gallery, optionValueId);
}

function showFallbackSlide(gallery, optionValueId) {
  // Only galleries that actually key slides to option values should snap back.
  // Otherwise every variant change would yank a browsing shopper to slide 0.
  const hasOptionSlides = getGallerySlides(gallery).some(
    (slide) => slide.dataset.ctOptionValueIds || slide.dataset.ctOptionValueId
  );
  if (!hasOptionSlides) return;

  const fallbackIndex = getGalleryFallbackSlideIndex(gallery);
  if (fallbackIndex < 0) return;

  // Track the intent, but not as a slide match: the slide doesn't represent this option.
  gallery.dataset.ctSelectedOptionValueId = '';
  gallery.dataset.ctFallbackOptionValueId = optionValueId || '';
  gallery.slideshow?.setAttribute('initial-slide', String(fallbackIndex));
  gallery.slideshow?.select(fallbackIndex, undefined, { animate: false });
}

function updateVariantUrl(variant) {
  // The event payload uses GraphQL ids (gid://shopify/ProductVariant/123), the storefront expects 123.
  const variantId = String(variant?.id ?? '').split('/').pop();
  if (!variantId) return;

  const url = new URL(window.location.href);
  if (url.searchParams.get('variant') === variantId) return;
  url.searchParams.set('variant', variantId);
  history.replaceState({}, '', url.toString());
}

// Apps such as Bundlex push their default variant into the picker on load: they check inputs and dispatch a
// synthetic change. Until the shopper interacts, keep a deliberately unselected picker unselected.
let shopperInteracted = false;
const markInteraction = (event) => {
  if (event.isTrusted) shopperInteracted = true;
};
document.addEventListener('pointerdown', markInteraction, true);
document.addEventListener('keydown', markInteraction, true);

document.addEventListener(
  'change',
  (event) => {
    if (event.isTrusted || shopperInteracted) return;

    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || picker.dataset.ctUnselected !== 'true' || getState(picker).size) return;

    const section = getSection(picker);
    if (section?.querySelector('variant-picker') !== picker) return;

    event.stopImmediatePropagation();
    applySectionState(section);
    // The app keeps updating the form after its event (it re-enables add to cart), so restore again after it.
    setTimeout(() => applySectionState(section));
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-ct-option-value-id]') : null;
  if (!target || !target.closest('media-gallery')) return;

  event.preventDefault();
  selectGalleryItem(target);
});

document.addEventListener('change', (event) => {
  const target = event.target;
  const picker = target instanceof HTMLElement ? target.closest('variant-picker') : null;
  if (!picker) return;

  const option = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
  if (!(option instanceof HTMLElement)) return;
  rememberOption(option);

  // Reflect the choice right away instead of waiting for the section re-render to come back.
  const section = getSection(picker);
  if (section?.querySelector('variant-picker') !== picker) return;
  const fieldset = option.closest('fieldset.variant-option');
  if (fieldset && option instanceof HTMLInputElement) setLegend(fieldset, option.value);
  showOptionInGallery(section, option.dataset.optionValueId);
});

document.addEventListener(StandardEvents.productSelect, (event) => {
  const section = getSection(event.target instanceof Element ? event.target : null);
  if (!section) return;
  const requestVersion = rememberGalleryIntent(section, event.detail?.optionValueId);

  event.promise
    .then(({ variant }) => {
      requestAnimationFrame(() => {
        applySectionState(section);
        restoreGallery(section, requestVersion);
        if (requestVersion === getGalleryState(section).selectionVersion && isSelectionComplete(section.querySelector('variant-picker'))) {
          updateVariantUrl(variant);
        }
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[casetime-product-gallery] Event promise rejected:', error);
    });
});

document.querySelectorAll('.shopify-section').forEach(applySectionState);
